import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BookIcon, VolumeIcon, CheckCircleIcon, StarIcon, PlayIcon, MicIcon, GraduationCapIcon, LightbulbIcon, ArrowRightIcon, HeadphonesIcon, InfoIcon } from 'lucide-react';
import { Tooltip } from './ui/Tooltip';
interface Section {
  id: string;
  title: {
    en: string;
    ar: string;
  };
  content: {
    en: React.ReactNode;
    ar: React.ReactNode;
  };
  icon: React.ElementType;
}
const VOWEL_SOUNDS = [{
  letter: 'A',
  short: {
    word: 'Cat',
    emoji: '🐱'
  },
  long: {
    word: 'Cake',
    emoji: '🍰'
  }
}, {
  letter: 'E',
  short: {
    word: 'Bed',
    emoji: '🛏'
  },
  long: {
    word: 'See',
    emoji: '👀'
  }
}, {
  letter: 'I',
  short: {
    word: 'Sit',
    emoji: '🪑'
  },
  long: {
    word: 'Time',
    emoji: '⏳'
  }
}, {
  letter: 'O',
  short: {
    word: 'Hot',
    emoji: '🔥'
  },
  long: {
    word: 'Home',
    emoji: '🏠'
  }
}, {
  letter: 'U',
  short: {
    word: 'Cup',
    emoji: '☕'
  },
  long: {
    word: 'Cute',
    emoji: '😊'
  }
}];
const CONSONANT_SOUNDS = [{
  letter: 'B',
  word: 'Boy',
  emoji: '👦'
}, {
  letter: 'D',
  word: 'Dog',
  emoji: '🐶'
}, {
  letter: 'K',
  word: 'Kite',
  emoji: '🪁'
}, {
  letter: 'M',
  word: 'Moon',
  emoji: '🌙'
}];
const GREETINGS = [{
  time: 'Morning',
  greeting: 'Good morning!',
  emoji: '🌞'
}, {
  time: 'Evening',
  greeting: 'Good evening!',
  emoji: '🌙'
}, {
  time: 'Anytime',
  greeting: 'Hello! / Hi!',
  emoji: '😊'
}];
const NUMBERS = Array.from({
  length: 10
}, (_, i) => ({
  number: i + 1,
  word: ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'][i],
  emoji: ['🐶', '🍎', '🏀', '📚', '✋', '🎲', '🌈', '🐙', '🦋', '🎂'][i],
  example: ['dog', 'apples', 'balls', 'books', 'fingers', 'dice', 'colors', 'tentacles', 'butterflies', 'candles'][i]
}));
export function LearningUnitContent() {
  const {
    isArabic
  } = useLanguage();
  const [activeSection, setActiveSection] = useState('sounds');
  const [playingSound, setPlayingSound] = useState<string | null>(null);
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  const playSound = (text: string) => {
    if ('speechSynthesis' in window) {
      setPlayingSound(text);
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.onend = () => setPlayingSound(null);
      window.speechSynthesis.speak(utterance);
    }
  };
  const markSectionComplete = (sectionId: string) => {
    setCompletedSections(prev => new Set([...prev, sectionId]));
  };
  const sections: Section[] = [{
    id: 'sounds',
    title: {
      en: 'English Sounds - Speak Like a Pro!',
      ar: 'الأصوات الإنجليزية - تحدث كالمحترفين!'
    },
    icon: MicIcon,
    content: {
      en: <div className="space-y-6" data-id="element-818">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4" data-id="element-819">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-820">
                Vowel Sounds – The Heart of English! ❤️
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-821">
                <div data-id="element-822">
                  <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2" data-id="element-823">
                    Short Vowel Sounds ⚡
                  </h4>
                  {VOWEL_SOUNDS.map(vowel => <button key={`short-${vowel.letter}`} onClick={() => playSound(vowel.short.word)} className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors mb-2" data-id="element-824">
                      <span className="flex items-center gap-2" data-id="element-825">
                        <span className="font-medium" data-id="element-826">{vowel.letter}</span>
                        <span data-id="element-827">
                          {vowel.emoji} {vowel.short.word}
                        </span>
                      </span>
                      <Tooltip content="Listen to pronunciation" data-id="element-828">
                        <VolumeIcon className={`w-4 h-4 ${playingSound === vowel.short.word ? 'text-blue-600 animate-pulse' : 'text-gray-400'}`} data-id="element-829" />
                      </Tooltip>
                    </button>)}
                </div>
                <div data-id="element-830">
                  <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2" data-id="element-831">
                    Long Vowel Sounds 🎵
                  </h4>
                  {VOWEL_SOUNDS.map(vowel => <button key={`long-${vowel.letter}`} onClick={() => playSound(vowel.long.word)} className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors mb-2" data-id="element-832">
                      <span className="flex items-center gap-2" data-id="element-833">
                        <span className="font-medium" data-id="element-834">{vowel.letter}</span>
                        <span data-id="element-835">
                          {vowel.emoji} {vowel.long.word}
                        </span>
                      </span>
                      <Tooltip content="Listen to pronunciation" data-id="element-836">
                        <VolumeIcon className={`w-4 h-4 ${playingSound === vowel.long.word ? 'text-blue-600 animate-pulse' : 'text-gray-400'}`} data-id="element-837" />
                      </Tooltip>
                    </button>)}
                </div>
              </div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4" data-id="element-838">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-839">
                Consonant Sounds 🎵
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-840">
                {CONSONANT_SOUNDS.map(consonant => <button key={consonant.letter} onClick={() => playSound(consonant.word)} className="flex flex-col items-center p-3 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors" data-id="element-841">
                    <span className="text-2xl mb-1" data-id="element-842">{consonant.emoji}</span>
                    <span className="font-medium" data-id="element-843">{consonant.letter}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-844">
                      {consonant.word}
                    </span>
                    <VolumeIcon className={`w-4 h-4 mt-1 ${playingSound === consonant.word ? 'text-green-600 animate-pulse' : 'text-gray-400'}`} data-id="element-845" />
                  </button>)}
              </div>
            </div>
            <button onClick={() => markSectionComplete('sounds')} className={`w-full mt-4 px-4 py-2 rounded-lg transition-colors ${completedSections.has('sounds') ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-846">
              {completedSections.has('sounds') ? isArabic ? 'تم إكمال القسم' : 'Section Completed' : isArabic ? 'إكمال القسم' : 'Complete Section'}
            </button>
          </div>,
      ar: <div className="space-y-6" data-id="element-847">
            {/* Mirror the English content structure with Arabic text */}
          </div>
    }
  }];
  const progress = completedSections.size / sections.length * 100;
  return <div className="max-w-7xl mx-auto" data-id="element-848">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-849">
        <div className="flex items-center justify-between mb-4" data-id="element-850">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-851">
            {isArabic ? 'تقدمك' : 'Your Progress'}
          </h2>
          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-852">
            {completedSections.size}/{sections.length}{' '}
            {isArabic ? 'مكتمل' : 'completed'}
          </span>
        </div>
        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-853">
          <div className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300" style={{
          width: `${progress}%`
        }} data-id="element-854" />
        </div>
      </div>
      <div className="flex overflow-x-auto space-x-4 mb-8 pb-2" data-id="element-855">
        {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`
              flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors
              ${activeSection === section.id ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}
            `} data-id="element-856">
            <section.icon className="w-5 h-5" data-id="element-857" />
            <span className="font-medium" data-id="element-858">
              {isArabic ? section.title.ar : section.title.en}
            </span>
            {completedSections.has(section.id) && <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-859" />}
          </button>)}
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-860">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-861">
          {isArabic ? sections.find(s => s.id === activeSection)?.title.ar : sections.find(s => s.id === activeSection)?.title.en}
        </h2>
        {isArabic ? sections.find(s => s.id === activeSection)?.content.ar : sections.find(s => s.id === activeSection)?.content.en}
      </div>
      <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6" data-id="element-862">
        <div className="flex items-start gap-3" data-id="element-863">
          <LightbulbIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" data-id="element-864" />
          <div data-id="element-865">
            <h3 className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-866">
              {isArabic ? 'نصيحة مفيدة! 💡' : 'Helpful Tip! 💡'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300" data-id="element-867">
              {isArabic ? 'تدرب على نطق هذه الأصوات يومياً للتحسين. استمع وكرر!' : 'Practice these sounds daily for improvement. Listen and repeat!'}
            </p>
          </div>
        </div>
      </div>
    </div>;
}